import { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { SortableDeliveryCard } from './SortableDeliveryCard';
import { DeliverySignatureDialog } from './DeliverySignatureDialog';
import { DeliveryWithDetails } from '@/types/delivery';
import { Button } from '@/components/ui/button';
import { Plus, Package } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useDeliveryMutations } from '@/hooks/useDeliveryMutations';

interface SortableDeliveryListProps {
  deliveries: DeliveryWithDetails[];
  onReorder?: (deliveries: DeliveryWithDetails[]) => void;
  onAddDelivery?: () => void;
  onDeliveryCompleted?: () => void;
}

export const SortableDeliveryList = ({
  deliveries: initialDeliveries,
  onReorder,
  onAddDelivery,
  onDeliveryCompleted,
}: SortableDeliveryListProps) => {
  const [deliveries, setDeliveries] = useState(initialDeliveries);
  const [signatureDelivery, setSignatureDelivery] = useState<DeliveryWithDetails | null>(null);
  const { updateDeliveryStatus } = useDeliveryMutations();

  // Sync local state when props change (e.g. after refetch)
  useEffect(() => {
    setDeliveries(initialDeliveries);
  }, [initialDeliveries]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = deliveries.findIndex((d) => d.id === active.id);
      const newIndex = deliveries.findIndex((d) => d.id === over.id);

      const newDeliveries = arrayMove(deliveries, oldIndex, newIndex);
      setDeliveries(newDeliveries);
      onReorder?.(newDeliveries);

      // Update route_order in database
      try {
        const updates = newDeliveries.map((delivery, index) => ({
          id: delivery.id,
          route_order: index + 1,
        }));

        for (const update of updates) {
          await supabase
            .from('deliveries')
            .update({ route_order: update.route_order })
            .eq('id', update.id);
        }
      } catch (error) {
        console.error('Erro ao salvar ordem:', error);
        toast.error('Erro ao salvar ordem das entregas');
      }
    }
  };

  const handleNavigate = async (delivery: DeliveryWithDetails) => {
    if (delivery.status === 'pending' || delivery.status === 'assigned') {
      try {
        await updateDeliveryStatus({
          deliveryId: delivery.id,
          status: 'in_transit',
          notes: 'Motorista iniciou navegação',
        });
      } catch (error) {
        console.error('Erro ao atualizar status:', error);
        toast.error('Erro ao iniciar entrega');
      }
    }
  };

  const handleComplete = (delivery: DeliveryWithDetails) => {
    setSignatureDelivery(delivery);
  };

  const handleSignatureSuccess = () => {
    setSignatureDelivery(null);
    onDeliveryCompleted?.();
  };

  if (deliveries.length === 0) {
    return (
      <div className="text-center py-12 space-y-4">
        <div className="h-16 w-16 mx-auto rounded-full bg-muted flex items-center justify-center">
          <Package className="h-8 w-8 text-muted-foreground" />
        </div>
        <div>
          <h3 className="font-semibold text-lg">Nenhuma entrega na rota</h3>
          <p className="text-sm text-muted-foreground">
            Adicione entregas à sua rota para começar
          </p>
        </div>
        {onAddDelivery && (
          <Button onClick={onAddDelivery} className="gap-2">
            <Plus className="h-4 w-4" />
            Adicionar Entrega
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {onAddDelivery && (
        <div className="flex justify-end">
          <Button onClick={onAddDelivery} variant="outline" className="gap-2">
            <Plus className="h-4 w-4" />
            Adicionar à Rota
          </Button>
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={deliveries.map((d) => d.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-3">
            {deliveries.map((delivery, index) => (
              <SortableDeliveryCard
                key={delivery.id}
                delivery={delivery}
                index={index}
                onNavigate={handleNavigate}
                onComplete={handleComplete}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {signatureDelivery && (
        <DeliverySignatureDialog
          open={!!signatureDelivery}
          onOpenChange={(open) => !open && setSignatureDelivery(null)}
          deliveryId={signatureDelivery.id}
          deliveryNumber={signatureDelivery.delivery_number}
          onSuccess={handleSignatureSuccess}
        />
      )}
    </div>
  );
};
